import React, { useMemo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles-team-auction-poster.css';

export function playerCaptainTag(player) {
  const r = (player.role || '').trim().toLowerCase();
  if (!r) return '';
  if (r.includes('vice')) return 'VC';
  if (r.includes('captain')) return 'C';
  return '';
}

export function splitAuctionTiers(players) {
  const sorted = [...players]
    .filter((p) => p.soldPrice != null && Number(p.soldPrice) > 0)
    .sort((a, b) => Number(b.soldPrice || 0) - Number(a.soldPrice || 0));
  const n = sorted.length;
  if (n === 0) return { tier1: [], tier2: [], tier3: [] };
  if (n <= 3) return { tier1: sorted, tier2: [], tier3: [] };
  if (n <= 9) {
    return { tier1: sorted.slice(0, 3), tier2: sorted.slice(3), tier3: [] };
  }
  return {
    tier1: sorted.slice(0, 3),
    tier2: sorted.slice(3, 9),
    tier3: sorted.slice(9)
  };
}

function playerKey(p) {
  if (p._id) return String(p._id);
  if (p.playerId) return String(p.playerId);
  const nm = (p.name || '').trim();
  return nm ? `${nm}|${p.soldPrice ?? ''}` : `unknown-${String(p.soldPrice ?? '')}`;
}

function PosterPlayerCard({ player, rank, buildPhotoUrl, topThree }) {
  const [imgFailed, setImgFailed] = useState(false);
  const photo = buildPhotoUrl(player.photo);
  const tag = playerCaptainTag(player);
  const price = Number(player.soldPrice || 0);
  const roleText = (player.role || '').trim();
  const placeText = (player.city || '').trim();
  const showPhoto = Boolean(photo) && !imgFailed;

  return (
    <div className="poster-pc">
      <div className="poster-pc__media">
        {showPhoto ? (
          <img
            src={photo}
            alt=""
            className="poster-pc__img"
            loading="eager"
            decoding="async"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="poster-pc__ph" aria-hidden="true">
            {(player.name || '?').charAt(0)}
          </div>
        )}
      </div>
      <div className="poster-pc__meta">
        <div className="poster-pc__name">{player.name}</div>
        <div className="poster-pc__price" aria-label={`Sold for ₹${price.toLocaleString('en-IN')}`}>
          <span className="poster-pc__rupee" aria-hidden="true">
            ₹
          </span>
          <span className="poster-pc__amount">{price.toLocaleString('en-IN')}</span>
        </div>
        {roleText || placeText ? (
          <div className="poster-pc__details">
            {roleText ? <span className="poster-pc__role-pill">{roleText}</span> : null}
            {placeText ? <span className="poster-pc__place">{placeText}</span> : null}
          </div>
        ) : null}
        {tag ? <span className="poster-pc__cap">{tag}</span> : null}
      </div>
    </div>
  );
}

export function TeamAuctionPosterSheet({
  team,
  tournament,
  purchasedPlayers,
  buildLogoUrl,
  buildPhotoUrl
}) {
  const sortedPlayers = useMemo(
    () =>
      [...(purchasedPlayers || [])]
        .filter((p) => p.soldPrice != null && Number(p.soldPrice) > 0)
        .sort((a, b) => Number(b.soldPrice || 0) - Number(a.soldPrice || 0)),
    [purchasedPlayers]
  );

  const rankByKey = useMemo(() => {
    const m = new Map();
    sortedPlayers.forEach((p, i) => {
      m.set(playerKey(p), i + 1);
    });
    return m;
  }, [sortedPlayers]);

  const teamLogo = team?.logo ? buildLogoUrl(team.logo) : '/default-logo.png';
  const tournamentLogo = tournament?.logo ? buildLogoUrl(tournament.logo) : null;
  const posterBgUrl =
    typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL
      ? encodeURI(`${process.env.PUBLIC_URL}/logo512.png`)
      : encodeURI('/logo512.png');

  const renderPlayers = (players) =>
    players.map((p) => {
      const rk = rankByKey.get(playerKey(p)) || 0;
      return (
        <PosterPlayerCard
          key={playerKey(p)}
          player={p}
          rank={rk}
          buildPhotoUrl={buildPhotoUrl}
        />
      );
    });

  return (
    <article className="auction-poster auction-poster-print" aria-label="Auction team poster">
      <div className="auction-poster__accent" aria-hidden="true" />
      <div className="auction-poster__bg" aria-hidden="true" style={{ '--poster-bg-url': `url("${posterBgUrl}")` }} />

      <header className="auction-poster__head">
        <div className="auction-poster__brand auction-poster__brand--team">
          <img src={teamLogo} alt="" />
        </div>
        <div className="auction-poster__titles">
          <div className="auction-poster__titlestack">
            <h1 className="auction-poster__team">{team?.name}</h1>
            {team?.group ? <div className="auction-poster__group">Group {team.group}</div> : null}
            <div className="auction-poster__event-wrap">
              <span className="auction-poster__event-rule" aria-hidden="true" />
              <p className="auction-poster__event">{tournament?.name || 'Tournament'}</p>
              <span className="auction-poster__event-rule" aria-hidden="true" />
            </div>
            {tournament?.location ? (
              <p className="auction-poster__venue">
                <span className="auction-poster__venue-dot" aria-hidden="true" />
                {tournament.location}
              </p>
            ) : null}
          </div>
        </div>
        <div className="auction-poster__brand auction-poster__brand--league">
          {tournamentLogo ? (
            <img src={tournamentLogo} alt="" />
          ) : (
            <span className="auction-poster__brand-fallback">{tournament?.code || '—'}</span>
          )}
        </div>
      </header>

      <section className="auction-poster__body">
        <div className="auction-poster__row auction-poster__row--grid">{renderPlayers(sortedPlayers)}</div>
      </section>

      <footer className="auction-poster__foot">
        <div className="auction-poster__foot-bar" aria-label="Generated by PlayLive Pro">
          <div className="auction-poster__brandline">
            <img className="auction-poster__app-logo" src="/logo192.png" alt="" />
            <div className="auction-poster__brandtext">
              <span className="auction-poster__credit-text">PlayLive Pro</span>
              <span className="auction-poster__credit-sub">Live auction • Team management</span>
            </div>
          </div>
        </div>
      </footer>
    </article>
  );
}

export default function TeamAuctionPosterModal({
  open,
  onClose,
  team,
  tournament,
  purchasedPlayers,
  buildLogoUrl,
  buildPhotoUrl
}) {
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    document.body.classList.add('team-poster-printing');
    document.documentElement.classList.add('team-poster-printing');
    return () => {
      document.body.classList.remove('team-poster-printing');
      document.documentElement.classList.remove('team-poster-printing');
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="poster-modal" role="dialog" aria-modal="true" aria-label="Team auction poster">
      <button type="button" className="poster-modal__scrim no-print" onClick={onClose} aria-label="Close" />
      <div className="poster-modal__sheet">
        <header className="poster-modal__bar no-print">
          <div className="poster-modal__bar-text">
            <span className="poster-modal__eyebrow">Preview</span>
            <span className="poster-modal__title">Team poster</span>
          </div>
          <div className="poster-modal__actions">
            <button type="button" className="poster-modal__btn poster-modal__btn--muted" onClick={onClose}>
              Close
            </button>
            <button type="button" className="poster-modal__btn poster-modal__btn--gold" onClick={() => window.print()}>
              Print
            </button>
          </div>
        </header>
        <div className="poster-modal__canvas">
          <div className="poster-modal__paper">
            <TeamAuctionPosterSheet
              team={team}
              tournament={tournament}
              purchasedPlayers={purchasedPlayers}
              buildLogoUrl={buildLogoUrl}
              buildPhotoUrl={buildPhotoUrl}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
